import { observer } from 'mobx-react';
import useAiContext from 'app/util/hooks/useAiContext';
import { formatRunoutDate } from './CapacityUtils';

const getPlanHighestUtil = (plan) => {
  const highest = plan.get('util')?.highest;
  return Number.isFinite(highest) ? highest : 0;
};

const getPlanRunout = (plan) => formatRunoutDate(plan.get('runout')?.earliest);

const getPlanThresholds = (plan) => plan.get('thresholds') || { utilization: {} };

export const CapacityAiProvider = observer(({ capacity, children }) => {
  // llm ability to recall information follows a U-shaped curve, so we want the highest severity plans to be listed first,
  // tie broken by utilization

  const sorted = (capacity.collection.models || []).slice().sort((a, b) => {
    const aWeight = capacity.severityTypes[a.get('severity') || 'healthy']?.weight ?? 0;
    const bWeight = capacity.severityTypes[b.get('severity') || 'healthy']?.weight ?? 0;

    if (bWeight !== aWeight) {
      return bWeight - aWeight;
    }
    return getPlanHighestUtil(b) - getPlanHighestUtil(a);
  });

  const hasCritical = sorted.some((p) => p.get('severity') === 'critical');
  const hasWarning = sorted.some((p) => p.get('severity') === 'warning');
  const highestUtil = sorted.length > 0 ? sorted[0] : null;

  const lines = sorted.map((p) => {
    const highest = p.get('util')?.highest;
    const earliest = getPlanRunout(p);
    const thresholds = getPlanThresholds(p);
    const criticalThreshold = thresholds.utilization?.critical;
    const warningThreshold = thresholds.utilization?.warning;
    const utilLabel = Number.isFinite(highest) ? `${highest.toFixed(2)}%` : 'N/A';
    const runoutLabel = earliest || 'N/A';
    return `${p.get('name')} — ${p.get('severity')} — Util: ${utilLabel} — Runout: ${runoutLabel} 
    (Thresholds: Warning: ${warningThreshold}%,  Critical: ${criticalThreshold}%)`;
  });

  const context = [
    'User is viewing the capacity planning overview page.',
    `Total Capacity Plans: ${capacity.collection.unfilteredSize}`,
    `Active/Filtered Plans: ${capacity.collection.size}`,
    'Plans in order of criticality:',
    ...lines
  ].join('\n');

  const suggestions = [
    hasCritical
      ? `What are my next steps for fixing the utilization shortage for "${highestUtil.get('name')}?"`
      : 'What can you tell me about my capacity plans?',
    hasWarning ? 'Which interfaces are running out of utilization?' : 'Where do I configure a new capacity plan?',
    'Which plans are nearing runout?',
    'Which plan has the earliest runout date?'
  ];

  useAiContext({ type: 'capacity', name: 'Capacity Planning Summary', context, suggestions });
  return children || null;
});
